import {isNumber} from 'lodash';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {EmptyCell} from '../../../components/common/table/cells/emptyCell';
import {NumberInputCell} from '../../../components/common/table/cells/numberInputCell';
import {SignatureCell} from '../../../components/common/table/cells/signatureCell';
import {TableColumn} from '../../../components/common/table/tableColumn';
import {TableRow, TableRowRenderOptions} from '../../../components/common/table/tableRow';
import {SignatureModel} from '../../../models/api/signatureModel';
import {SignaturesTableColumnsEnum} from './signaturesTableConfig';

/*
 * Props.
 */

interface SignaturesTableRowProps {
  signature?: SignatureModel;
  options: TableRowRenderOptions;
  tableColumns: ReadonlyArray<TableColumn<SignaturesTableColumnsEnum>>;
  isSelected: boolean;
  isInMultiSelectMode: boolean;
  isNextSiblingSelected: boolean;
  isPreviousSiblingSelected: boolean;
  onSelect: Handler;
  onMultiSelect: Handler;
  onCheckBoxToggle: HandlerOf<boolean>;
  moveSignature: (signatureId: number, offset: number, shiftPressed: boolean) => void;
  moveSignaturePosition: (signatureId: number, target: number) => void;
  clientSortOrder: number;
  maxSortNumber?: number;
}

/*
 * Style.
 */

interface StyledTableRowProps {
  $minHeight: number;
}
const StyledTableRow = styled(TableRow)<StyledTableRowProps>`
  min-height: ${(p) => p.$minHeight}px;
`;

/*
 * Component.
 */

export const SignaturesTableRow: FC<SignaturesTableRowProps> = (props) => {
  const {
    signature,
    options,
    tableColumns,
    isSelected,
    isInMultiSelectMode,
    isPreviousSiblingSelected,
    isNextSiblingSelected,
    onCheckBoxToggle,
    onMultiSelect,
    onSelect,
    moveSignature,
    moveSignaturePosition,
    maxSortNumber,
    clientSortOrder,
  } = props;

  const [sortOrder, setSortOrder] = useState<number | undefined>(clientSortOrder);
  useEffect(() => {
    setSortOrder(clientSortOrder);
  }, [clientSortOrder]);

  if (!signature) {
    return null;
  }

  const cellRenderer = (
    index: number,
    column: TableColumn<SignaturesTableColumnsEnum>,
    isHovering: boolean,
    shouldShowStickyGradient: boolean,
  ) => {
    switch (column.type) {
      case SignaturesTableColumnsEnum.NAME:
        return (
          <SignatureCell
            key={SignaturesTableColumnsEnum.NAME}
            value={signature}
            settings={column.settings}
            size={options.size}
            isHovering={isHovering}
            isSelected={isSelected}
            index={index}
            avatarSize={VisualSizesEnum.LARGE}
            shouldShowStickyGradient={shouldShowStickyGradient}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
          />
        );
      case SignaturesTableColumnsEnum.ORDER: {
        return (
          <NumberInputCell
            key={SignaturesTableColumnsEnum.ORDER}
            value={sortOrder}
            settings={column.settings}
            size={options.size}
            maxSortNumber={maxSortNumber}
            isHovering={isHovering}
            isSelected={isSelected}
            isInMultiSelectMode={isInMultiSelectMode}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            onIncrement={(event) => {
              event.preventDefault();
              moveSignature(signature.id, -1, event.shiftKey);
            }}
            onDecrement={(event) => {
              event.preventDefault();
              moveSignature(signature.id, 1, event.shiftKey);
            }}
            onChange={(value) => {
              if (isNumber(value) && value > 0) {
                setSortOrder(value);
              } else {
                setSortOrder(undefined);
              }
            }}
            onBlur={(event) => {
              event.preventDefault();
              setSortOrder(clientSortOrder);
            }}
            onKeyDown={(event) => {
              if (event.key === Key.Enter) {
                if (typeof sortOrder === 'number') {
                  moveSignaturePosition(signature.id, sortOrder);
                } else {
                  setSortOrder(clientSortOrder);
                }
              }
            }}
          />
        );
      }
      default: {
        return (
          <EmptyCell
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
          />
        );
      }
    }
  };

  return (
    <StyledTableRow
      onSelect={(event) => {
        if (event.defaultPrevented) {
          return;
        }
        event.preventDefault();

        // If they CMD click, treat the same as checking the checkbox.
        if (event.metaKey) {
          onCheckBoxToggle(!isSelected);
          return;
        }
        // If they shift click, issue a multi select.
        if (event.shiftKey) {
          onMultiSelect();
          return;
        }
        // If we are in multi select mode, treat this as toggling the checkbox.
        if (isInMultiSelectMode) {
          onCheckBoxToggle(!isSelected);
          return;
        }
        // Default to selecting the row.
        onSelect();
      }}
      onCheckBoxClick={(event) => {
        if (event.defaultPrevented) {
          return;
        }
        event.preventDefault();
        onCheckBoxToggle(!isSelected);
      }}
      renderColumn={(isHovering) =>
        tableColumns.map((column, index) =>
          cellRenderer(index, column, isHovering, Boolean(options.shouldShowStickyGradient)),
        )
      }
      hasHover
      $minHeight={options.tableRowHeight}
      shouldShowRightGradient={options.shouldShowRightGradient}
      isSelected={isSelected}
      shouldAllowMultiSelect={options.shouldAllowMultiSelect}
      isCheckboxesVisible={isInMultiSelectMode}
    />
  );
};
